import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from "@angular/core";
import {DecimalPipe} from "@angular/common";
import {ModeratAnfrage} from "../anfrage/moderat.anfrage";
import {FinanzierungsTyp, SliderConfig} from "../interfaces/result-data.interface";
import {FormGroup} from "@angular/forms";

/**
 * Component for displaying financing options and costs.
 */
@Component({
    selector: 'app-moderat-sanierung-financing',
    templateUrl: './sanierung-financing.component.html',
    providers: [DecimalPipe],
    standalone: false
})
export class SanierungFinancingComponent implements OnInit, OnChanges{
  /**
   * request object
   */
  @Input() anfrage: ModeratAnfrage;

  /**
   * Selected financing type
   */
  @Input() financing: FinanzierungsTyp;

  /**
   * Flag indicating whether data is currently loading (disables interactions in the template).
   */
  @Input() loading: boolean;

  /**
   * Reactive form group containing financing controls.
   */
  @Input() financialForm: FormGroup;

  /**
   * Emits when a measure selection change should be propagated to the parent component.
   * true = user actively changed a measure; false = programmatic change (e.g., via buttons).
   */
  @Output() measureSelectionChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * eventemitter of the financing selection changes.
   */
  @Output() financingChange: EventEmitter<FinanzierungsTyp> = new EventEmitter<FinanzierungsTyp>();

  /**
   * Configuration object for the equity slider (min, max, step, etc.).
   */
  sliderConfig: SliderConfig = {min: 0, max: 1000, step: 1000};

  /**
   * Translation key prefix for the financing section.
   */
  textkeyPrefix = 'moderat.sanierung.massnahmen.section.costs.finanzierung';

  /**
   * Default value of the interest rate, will be overwritten by the input
   */
  interestRateDefault = 3.5;

  /**
   * UI toggle flags for showing/hiding help sections and cost details.
   */
  toggle = {
    helpZuschuss: false,
    helpBankkredit: false,
    helpZinssatz: false,
    helpRate: false,
    helpRest: false,
    sideHelp: false
  };

  /**
   * Timeout for change of finance version
   */
  financeSwitch = false;

  /**
   * Timer for the increase/decrease buttons.
   */
  saveTimer: any;

  /**
   * Math object for convenience.
   * @protected
   */
  protected readonly Math = Math;

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    this.updateSliderConfig();
    this.updateInterestRate();
  }

  /**
   * @inheritDoc
   */
  ngOnChanges(changes: SimpleChanges): void {
    // update the slider config if data from the backend changes.
    if(changes.loading && changes.loading.currentValue === false && changes.loading.firstChange === false) {
      this.updateSliderConfig();
      this.updateInterestRate();
    }

    if(changes.financing && changes.financing.isFirstChange() === false) {
      this.financeSwitch = true;
      setTimeout(() => this.financeSwitch = false, 500);
    }
  }

  /**
   * Update the equity share (eigenkapital) when the slider is moved.
   * @param event DOM or component event carrying the new numeric value in event.target.value.
   */
  onSliderChange(event: any): void {
    if(event.target?.value) {
      this.anfrage.finanzierungInput.eigenkapital =
        Math.min(event.target.value * 1, this.getMaxOwnEquity());
    }
  }

  onFinancingChange(financing: FinanzierungsTyp): void {
    this.financing = financing;
    this.updateSliderConfig();
    this.financingChange.emit(financing);
  }

  /**
   * Notify parent listeners that the selected measure has changed.
   * @param measureChange Whether the change was initiated by a user action (default true).
   */
  onMeasureSelectionChange(measureChange: boolean = true): void {
    this.measureSelectionChange.emit(measureChange);
  }

  /**
   * Increase the interest rate (zinssatz) by 0.5 percentage points up to a maximum of 10.0.
   * Also emits a non-user-initiated measure change to update dependent calculations.
   */
  onIncrease(): void {
    this.resetSaveTimer();
    const currentValue = parseFloat(this.financialForm.get('zinssatz').value);

    if(currentValue < 10) {
      this.financialForm.get('zinssatz').setValue((currentValue + 0.1).toFixed(1));
      this.saveChanges();
    }
  }

  /**
   * Decrease the interest rate (zinssatz) by 0.5 percentage points down to a minimum of 0.5.
   * Also emits a non-user-initiated measure change to update dependent calculations.
   */
  onDecrease(): void {
    this.resetSaveTimer();
    const currentValue = parseFloat(this.financialForm.get('zinssatz').value);

    if(currentValue > 0.5) {
      this.financialForm.get('zinssatz').setValue((currentValue - 0.1).toFixed(1));
      this.saveChanges();
    }
  }

  /**
   * Save changes with a little timer.
   */
  saveChanges(): void {
    this.saveTimer = setTimeout(() => this.onMeasureSelectionChange(false), 500);
  }

  /**
   * Reset the timer for saving changes.
   */
  resetSaveTimer(): void {
    if(this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
  }

  /**
   * Reset the interest rate (zinssatz) to the default value.
   */
  onReset(): void {
    this.financialForm.get('zinssatz').setValue((this.interestRateDefault).toFixed(1));
    this.onMeasureSelectionChange(false);
  }

  /**
   * Set config for the sliders
   */
  updateSliderConfig(): void {
    // set inputvalue
    this.anfrage.finanzierungInput.eigenkapital = this.anfrage.measureResult.costs.financing[this.financing].eigenkapital ?? 0;
    this.financialForm.get('eigenkapital').setValue(this.anfrage.finanzierungInput.eigenkapital);

    const roundUp1000 = function(value: number) {
      return Math.ceil(value / 1000) * 1000;
    };
    const max = this.getMaxOwnEquity();
    this.sliderConfig.max = roundUp1000(max ?? 0);
  }

  /**
   * calculate the maximum equity that can be invested
   * @returns
   */
  getMaxOwnEquity(): number {
    const overallCosts = this.anfrage.measureResult.costs.overall.sum;
    const funding = this.anfrage.measureResult.costs.financing[this.financing].funding_grants.sum;
    return Math.max(0, overallCosts - funding);
  }

  /**
   * Save the default interest rate and update the form with the current value.
   */
  updateInterestRate(): void {
    // get the default
    this.interestRateDefault = this.anfrage.measureResult.costs.financing.bankkredit_zinssatz_default;

    // get the value from the current selected financing
    this.financialForm.get('zinssatz').setValue(this.anfrage.measureResult.costs.financing[this.financing].bankkredit.zinssatz.toFixed(1));
  }
}
